/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

/*
 * The Map Viewer's event bus.
 *
 * Two channels, on purpose. `emit` dispatches a `CustomEvent` on the
 * document, which is how anything holding a DOM reference hears about it,
 * and it calls the subscribers registered through `on`, which is how a
 * module that has no element to listen on hears about it.
 *
 * It used to fan out to a third channel: `jQuery(target).trigger(name)`.
 * That existed for the visit counter and the paywall lightbox, which bound
 * their listeners with jQuery. They subscribe through `on` now, so the
 * bridge and its dependency are gone.
 */

const subscribers = new Map();

/**
 * Subscribe to an event.
 *
 * @param {string} name - Event name.
 * @param {function(...any): void} handler - Called with the event detail.
 */
export function on(name, handler) {
    if (!subscribers.has(name)) {
        subscribers.set(name, []);
    }
    subscribers.get(name).push(handler);
}

/**
 * Unsubscribe from an event.
 *
 * @param {string} name - Event name.
 * @param {function(...any): void} handler - The handler to remove.
 */
export function off(name, handler) {
    const list = subscribers.get(name);
    if (!list) return;
    const index = list.indexOf(handler);
    if (index >= 0) list.splice(index, 1);
}

/**
 * Announce an event on both channels.
 *
 * A handler that throws is reported and does not stop the others: one
 * broken subscriber must not silence every listener after it.
 *
 * @param {EventTarget} target - Kept for call-site symmetry; the native
 *   event is dispatched on the document so listeners need no reference to
 *   whichever element caused it.
 * @param {string} name - Event name.
 * @param {any[]} [detail] - Arguments for the subscribers, and the
 *   `detail` of the native event.
 */
export function emit(target, name, detail = []) {
    document.dispatchEvent(new CustomEvent(name, { detail }));

    for (const handler of [
        ...(targetSubscribers.get(target)?.get(name) || []),
    ]) {
        try {
            handler.apply(target, detail);
        } catch (error) {
            console.error(error);
        }
    }

    for (const handler of [...(subscribers.get(name) || [])]) {
        try {
            handler(...detail);
        } catch (error) {
            console.error(error);
        }
    }
}

/*
 * Per-target subscriptions.
 *
 * The bus above is global: a subscriber hears an event whoever raised it.
 * The map's two kinds of popup both announce `show.popup`, and the page
 * treats them differently, so it needs to say which one it means. jQuery
 * gave that for free by binding to the object; this is the replacement.
 */
const targetSubscribers = new WeakMap();

/**
 * Subscribe to an event raised by one particular object.
 *
 * @param {object} target - The object to listen to.
 * @param {string} name - Event name.
 * @param {function(...any): void} handler - Called with the event detail,
 *   bound to `target` so `this` reads as it did before.
 */
export function onTarget(target, name, handler) {
    if (!targetSubscribers.has(target)) {
        targetSubscribers.set(target, new Map());
    }
    const byName = targetSubscribers.get(target);
    if (!byName.has(name)) {
        byName.set(name, []);
    }
    byName.get(name).push(handler);
}

/**
 * Unsubscribe from one object's event.
 *
 * @param {object} target - The object listened to.
 * @param {string} name - Event name.
 * @param {function(...any): void} handler - The handler to remove.
 */
export function offTarget(target, name, handler) {
    const list = targetSubscribers.get(target)?.get(name);
    if (!list) return;
    const index = list.indexOf(handler);
    if (index >= 0) list.splice(index, 1);
}

/** Forget every subscriber. Intended for tests. */
export function resetEvents() {
    subscribers.clear();
}
